import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ToastService } from '../../services/toast.service';
import { MemberService } from '../member-service/MemberService ';

declare var $: any;

type EventDateFormat = 'monthYear' | 'timestamp';

interface HbA1cFormSnapshot {
  hbA1cValue: number | null;
  eventDateFormat: EventDateFormat;
  eventMonth: number | null;
  eventYear: number | null;
  eventDate: string;
  eventTime: string;
}

@Component({
  selector: 'app-log-hba1c',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatPaginatorModule],
  templateUrl: './log-hba1c.component.html',
  styleUrls: ['./log-hba1c.component.css']
})
export class LogHba1cComponent {
  memberId: string = '';

  hbA1cValue: number | null = null;
  eventDateFormat: EventDateFormat = 'monthYear';
  eventMonth: number | null = null; // 1-12
  eventYear: number | null = null;
  eventDate: string = ''; // yyyy-MM-dd
  eventTime: string = ''; // HH:mm

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['HbA1cValue', 'EventDate', 'Source', 'LoggedBy', 'ReportedDate', 'Action'];
  pageSize: number = 10;
  pageIndex: number = 0;
  totalRecords: number = 0;
  isLoading: boolean = false;
  isSaving: boolean = false;
  isEditMode: boolean = false;
  logToDelete: any = null;
  isDeleting: boolean = false;
  editingLog: any = null;
  originalForm: HbA1cFormSnapshot | null = null;
  maxDate: string = '';

  months = [
    { value: 1, name: 'January' },
    { value: 2, name: 'February' },
    { value: 3, name: 'March' },
    { value: 4, name: 'April' },
    { value: 5, name: 'May' },
    { value: 6, name: 'June' },
    { value: 7, name: 'July' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'October' },
    { value: 11, name: 'November' },
    { value: 12, name: 'December' }
  ];
  years: number[] = [];

  constructor(
    private memberService: MemberService,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.memberId = this.route.snapshot.queryParamMap.get('id') || '';

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    // Current year back to 20 years ago
    const currentYear = today.getFullYear();
    for (let year = currentYear; year >= currentYear - 20; year--) {
      this.years.push(year);
    }

    if (this.memberId) {
      this.loadHbA1cLogs();
    }
  }

  loadHbA1cLogs(pageNo: number = 1): void {
    this.pageIndex = pageNo - 1;
    this.isLoading = true;
    this.dataSource = new MatTableDataSource<any>([]);
    this.memberService.getHbA1cLogsByMemberId(this.memberId, pageNo, this.pageSize).subscribe({
      next: (response) => {
        this.isLoading = false;
        const data = response?.result ?? response;
        const logs = Array.isArray(data) ? data : (data?.items ?? data?.data ?? data?.list ?? []);
        this.dataSource = new MatTableDataSource<any>(logs);
        this.totalRecords = data?.totalCount ?? data?.count ?? data?.totalRecords
          ?? response?.totalCount ?? response?.count ?? response?.totalRecords ?? logs.length;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading HbA1c logs:', error);
        this.toast.errorMessage(this.getApiErrorMessage(error, 'Failed to load HbA1c logs.'));
        this.dataSource = new MatTableDataSource<any>([]);
        this.totalRecords = 0;
      }
    });
  }

  // Message sent by the API in an error response, if any
  private getApiErrorMessage(error: any, fallback: string): string {
    return error?.error?.message || fallback;
  }

  pageChangeEvent(page: PageEvent): void {
    this.loadHbA1cLogs(page.pageIndex + 1);
  }

  // Event date as entered: full timestamp, or month and year
  formatEventDate(log: any): string {
    if (log.eventDate) {
      const [date, time] = String(log.eventDate).split('T');
      const [year, month, day] = date.split('-').map(Number);
      const monthName = this.months.find(m => m.value === month)?.name ?? '';
      const dateText = `${day} ${monthName} ${year}`;
      return time ? `${dateText}, ${time.substring(0, 5)}` : dateText;
    }
    if (log.eventMonth && log.eventYear) {
      const monthName = this.months.find(m => m.value === Number(log.eventMonth))?.name ?? '';
      return `${monthName} ${log.eventYear}`;
    }
    return '---';
  }

  openAddLogModal(): void {
    this.isEditMode = false;
    this.editingLog = null;
    this.resetForm();
    $('#addHba1cLogModal').modal('show');
  }

  openEditLogModal(log: any): void {
    this.isEditMode = true;
    this.editingLog = log;
    this.resetForm();

    this.hbA1cValue = log.hbA1cValue ?? null;
    if (log.eventDate && !log.eventMonth && !log.eventYear) {
      // Timestamp log: split "yyyy-MM-ddTHH:mm:ss" into date and time fields
      this.eventDateFormat = 'timestamp';
      const [date, time] = String(log.eventDate).split('T');
      this.eventDate = date ?? '';
      this.eventTime = time ? time.substring(0, 5) : '';
    } else {
      this.eventDateFormat = 'monthYear';
      this.eventMonth = log.eventMonth ? Number(log.eventMonth) : null;
      this.eventYear = log.eventYear ? Number(log.eventYear) : null;
      if (this.eventYear && !this.years.includes(this.eventYear)) {
        this.years.push(this.eventYear);
        this.years.sort((a, b) => b - a);
      }
    }

    this.originalForm = this.getFormSnapshot();
    $('#addHba1cLogModal').modal('show');
  }

  closeAddLogModal(): void {
    $('#addHba1cLogModal').modal('hide');
    this.isEditMode = false;
    this.editingLog = null;
    this.resetForm();
  }

  onEventDateFormatChange(): void {
    this.eventMonth = null;
    this.eventYear = null;
    this.eventDate = '';
    this.eventTime = '';
  }

  // Months after the current month are frozen unless a past year is selected
  isFutureMonth(month: number): boolean {
    const now = new Date();
    if (this.eventYear && Number(this.eventYear) < now.getFullYear()) {
      return false;
    }
    return month > now.getMonth() + 1;
  }

  onEventYearChange(): void {
    if (this.eventMonth && this.isFutureMonth(this.eventMonth)) {
      this.eventMonth = null;
    }
  }

  // Allow only digits and a single decimal point in the HbA1c field
  onValueKeyDown(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  private getFormSnapshot(): HbA1cFormSnapshot {
    return {
      hbA1cValue: this.hbA1cValue === null || this.hbA1cValue === undefined || (this.hbA1cValue as any) === '' ? null : Number(this.hbA1cValue),
      eventDateFormat: this.eventDateFormat,
      eventMonth: this.eventMonth,
      eventYear: this.eventYear,
      eventDate: this.eventDate,
      eventTime: this.eventTime
    };
  }

  // Update Log stays disabled until something differs from the row's original values
  get hasChanges(): boolean {
    if (!this.isEditMode || !this.originalForm) {
      return true;
    }
    const current = this.getFormSnapshot();
    return (Object.keys(current) as (keyof HbA1cFormSnapshot)[]).some(key => current[key] !== this.originalForm![key]);
  }

  // Returns an error message if any field is empty or invalid, otherwise ''
  private validateForm(): string {
    if (this.hbA1cValue === null || this.hbA1cValue === undefined || (this.hbA1cValue as any) === '') {
      return 'HbA1c Value is required.';
    }
    if (isNaN(Number(this.hbA1cValue)) || Number(this.hbA1cValue) <= 0) {
      return 'Please enter a valid HbA1c Value.';
    }
    if (!this.eventDateFormat) {
      return 'Log Event Date As is required.';
    }

    if (this.eventDateFormat === 'monthYear') {
      if (!this.eventMonth) {
        return 'Event Month is required.';
      }
      if (!this.eventYear) {
        return 'Event Year is required.';
      }
      const now = new Date();
      if (this.eventYear === now.getFullYear() && this.eventMonth > now.getMonth() + 1) {
        return 'Event month cannot be in the future.';
      }
    } else {
      if (!this.eventDate) {
        return 'Event Date is required.';
      }
      if (!this.eventTime) {
        return 'Event Time is required.';
      }
      const eventDateTime = new Date(`${this.eventDate}T${this.eventTime}`);
      if (isNaN(eventDateTime.getTime())) {
        return 'Please select a valid Event Date and Event Time.';
      }
      if (eventDateTime > new Date()) {
        return 'Event date cannot be in the future.';
      }
    }
    return '';
  }

  // Selected date and time as is (no UTC conversion)
  private getEventDateValue(): string {
    return `${this.eventDate}T${this.eventTime}:00`;
  }

  submitLog(): void {
    if (!this.memberId) {
      this.toast.errorMessage('Member not found.');
      return;
    }
    const validationError = this.validateForm();
    if (validationError) {
      this.toast.errorMessage(validationError);
      return;
    }

    if (this.isEditMode) {
      this.updateLog();
    } else {
      this.addLog();
    }
  }

  private addLog(): void {
    const payload: any = {
      memberId: this.memberId,
      hbA1cValue: Number(this.hbA1cValue),
      eventDate: null,
      eventMonth: null,
      eventYear: null
    };

    if (this.eventDateFormat === 'monthYear') {
      payload.eventMonth = Number(this.eventMonth);
      payload.eventYear = Number(this.eventYear);
    } else {
      payload.eventDate = this.getEventDateValue();
    }

    this.isSaving = true;
    this.memberService.createHbA1cLog(payload).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.toast.successMessage(response?.message || 'HbA1c log added successfully!');
        this.closeAddLogModal();
        this.loadHbA1cLogs();
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error adding HbA1c log:', error);
        this.toast.errorMessage(this.getApiErrorMessage(error, 'Failed to add HbA1c log. Please try again.'));
      }
    });
  }

  private updateLog(): void {
    const documentId = this.editingLog?.id ?? this.editingLog?._id;
    if (!documentId || !this.originalForm) {
      this.toast.errorMessage('Unable to find the log to update.');
      return;
    }

    // Send only the fields that changed
    const original = this.originalForm;
    const current = this.getFormSnapshot();
    const changedFields: any = {};

    if (current.hbA1cValue !== original.hbA1cValue) {
      changedFields.hbA1cValue = current.hbA1cValue;
    }

    if (current.eventDateFormat !== original.eventDateFormat) {
      // Format switched: clear the old format's fields and send the new ones
      if (current.eventDateFormat === 'monthYear') {
        changedFields.eventDate = null;
        changedFields.eventMonth = Number(current.eventMonth);
        changedFields.eventYear = Number(current.eventYear);
      } else {
        changedFields.eventMonth = null;
        changedFields.eventYear = null;
        changedFields.eventDate = this.getEventDateValue();
      }
    } else if (current.eventDateFormat === 'monthYear') {
      if (current.eventMonth !== original.eventMonth) {
        changedFields.eventMonth = Number(current.eventMonth);
      }
      if (current.eventYear !== original.eventYear) {
        changedFields.eventYear = Number(current.eventYear);
      }
    } else if (current.eventDate !== original.eventDate || current.eventTime !== original.eventTime) {
      changedFields.eventDate = this.getEventDateValue();
    }

    if (Object.keys(changedFields).length === 0) {
      this.toast.errorMessage('No changes to update.');
      return;
    }

    this.isSaving = true;
    this.memberService.updateHbA1cLog(documentId, this.memberId, changedFields).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.toast.successMessage(response?.message || 'HbA1c log updated successfully!');
        this.closeAddLogModal();
        this.loadHbA1cLogs(this.pageIndex + 1);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating HbA1c log:', error);
        this.toast.errorMessage(this.getApiErrorMessage(error, 'Failed to update HbA1c log. Please try again.'));
      }
    });
  }

  openDeleteLogModal(log: any): void {
    this.logToDelete = log;
    $('#deleteHba1cLogModal').modal('show');
  }

  closeDeleteLogModal(): void {
    $('#deleteHba1cLogModal').modal('hide');
    this.logToDelete = null;
  }

  confirmDeleteLog(): void {
    const documentId = this.logToDelete?._id ?? this.logToDelete?.id;
    if (!documentId || !this.memberId) {
      this.toast.errorMessage('Unable to find the log to delete.');
      return;
    }

    this.isDeleting = true;
    this.memberService.deleteHbA1cLog(documentId, this.memberId).subscribe({
      next: (response) => {
        this.isDeleting = false;
        this.closeDeleteLogModal();
        this.toast.successMessage(response?.message || 'HbA1c log deleted successfully!');
        // Step back a page if the last row on this page was deleted
        const pageNo = this.dataSource.data.length === 1 && this.pageIndex > 0 ? this.pageIndex : this.pageIndex + 1;
        this.loadHbA1cLogs(pageNo);
      },
      error: (error) => {
        this.isDeleting = false;
        console.error('Error deleting HbA1c log:', error);
        this.toast.errorMessage(this.getApiErrorMessage(error, 'Failed to delete HbA1c log. Please try again.'));
      }
    });
  }

  resetForm(): void {
    this.hbA1cValue = null;
    this.eventDateFormat = 'monthYear';
    this.originalForm = null;
    this.onEventDateFormatChange();
  }
}
